/* eslint-disable import/no-extraneous-dependencies */
import { useEffect, useRef, useState } from 'react';

import { buildCFSReportDefinition } from '@/pages/CFS/reports/cfsReport/buildCFSReportDefinition';
import type { CFSReportContext } from '@/pages/CFS/reports/cfsReport/types';

import type { CFSRedactionsMap } from '../data';

const DEBOUNCE_MS = 300;

// Cache pdfMake imports across calls so the dynamic-import cost is paid once.
let pdfMakePromise: Promise<any> | null = null;
let pdfFontsPromise: Promise<any> | null = null;

const loadPdfMake = async () => {
  if (!pdfMakePromise) pdfMakePromise = import('pdfmake/build/pdfmake');
  if (!pdfFontsPromise) pdfFontsPromise = import('pdfmake/build/vfs_fonts');
  const [pdfMakeModule, pdfFontsModule] = await Promise.all([
    pdfMakePromise,
    pdfFontsPromise,
  ]);
  const pdfMakeInstance = (pdfMakeModule as any).default || pdfMakeModule;
  const pdfFontsInstance = (pdfFontsModule as any).default || pdfFontsModule;
  if (pdfMakeInstance && pdfFontsInstance) {
    pdfMakeInstance.vfs = pdfFontsInstance.pdfMake?.vfs || pdfFontsInstance;
  }
  return pdfMakeInstance;
};

interface UseCFSPDFArgs {
  ctx: CFSReportContext | undefined;
  redactions: CFSRedactionsMap | undefined;
}

interface UseCFSPDFReturn {
  pdfBlob: Blob | null;
  pdfUrl: string | null;
  loading: boolean;
  error: string | null;
}

export const useCFSPDF = ({
  ctx,
  redactions,
}: UseCFSPDFArgs): UseCFSPDFReturn => {
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!ctx) return undefined;

    let isMounted = true;
    const debounceHandle = window.setTimeout(async () => {
      setLoading(true);
      setError(null);

      try {
        const definition = await buildCFSReportDefinition({ ctx, redactions });
        const pdfMakeInstance = await loadPdfMake();
        const createPdf = pdfMakeInstance.createPdf || pdfMakeInstance;
        const pdfDocGenerator = createPdf(definition);

        pdfDocGenerator.getBlob((blob: Blob) => {
          if (!isMounted) return;
          const nextUrl = URL.createObjectURL(blob);
          // Revoke previous URL to avoid object URL leaks on redaction changes.
          if (currentUrlRef.current) {
            URL.revokeObjectURL(currentUrlRef.current);
          }
          currentUrlRef.current = nextUrl;
          setPdfBlob(blob);
          setPdfUrl(nextUrl);
          setLoading(false);
        });
      } catch (err: any) {
        if (!isMounted) return;
        setError(err?.message || 'Failed to generate PDF');
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      isMounted = false;
      window.clearTimeout(debounceHandle);
    };
  }, [ctx, redactions]);

  // Final cleanup on unmount: revoke last URL.
  useEffect(
    () => () => {
      if (currentUrlRef.current) {
        URL.revokeObjectURL(currentUrlRef.current);
        currentUrlRef.current = null;
      }
    },
    [],
  );

  return { pdfBlob, pdfUrl, loading, error };
};
